/**
 * Frontend Prompt-Builder
 *
 * Neue Architektur:
 * - Der MASTER PROMPT ist nur im Backend (unsichtbar für User)
 * - Im Frontend wird nur der SECTION PROMPT angezeigt/editiert
 * - Formulardaten werden als Kontext angehängt
 *
 * Der /api/prompts/generate-v2 Endpunkt fügt dann
 * MASTER + SECTION + INPUT zusammen.
 */

/**
 * Section-Prompt-Config (Frontend-Kopie für Default-Prompts).
 * Wird nur als Fallback verwendet, wenn Backend nicht erreichbar.
 */
export interface SectionPromptConfig {
  cardId: string;
  title: string;
  defaultPrompt: string;
  requiredFields: string[];
  optionalFields: string[];
}

export const SECTION_PROMPT_DEFAULTS: Record<string, SectionPromptConfig> = {
  'card-1-1': {
    cardId: 'card-1-1',
    title: '1.1 Beratungsauftrag und Grund / Anlass',
    requiredFields: ['consulting-purpose'],
    optionalFields: ['org-name', 'wirtschaftszweig', 'rechtsform', 'ort', 'geschaeftsgegenstand', 'zielsetzung'],
    defaultPrompt: `Erstelle den Berichtstext für Abschnitt 1.1:
\u201EBeratungsauftrag und Grund / Anlass\u201C.

ZIEL DES ABSCHNITTS
Beschreibe den Anlass der Beratung, den konkreten Beratungsauftrag sowie die betriebliche Ausgangssituation so, dass der Beratungsbedarf sachlich, nachvollziehbar und förderfähig dargestellt wird.

AUFGABE
Formuliere einen professionellen, verständlichen Fließtext für einen BAFA-Beratungsbericht auf Basis der bereitgestellten Eingaben.

PRIORITÄT
- Abschnittsspezifische Regeln haben Vorrang vor allgemeinen System- oder Master-Anweisungen
- Bei Unsicherheit IMMER allgemeiner formulieren, niemals konkretisieren

WICHTIGE VERWENDUNG DER EINGABEN
- Nutze die direkten Formulareingaben immer vorrangig
- Nutze die Website-Analyse nur ergänzend zur Einordnung der Ausgangssituation
- Wenn sich Eingaben widersprechen, haben Formulareingaben Vorrang
- Verwende nur Aussagen, die eindeutig ableitbar sind

WICHTIGE SICHERHEITSREGELN (SEHR WICHTIG)
- KEINE konkreten Gesetze, Vorschriften oder rechtlichen Rahmenbedingungen nennen
- KEINE Aussagen über gesellschaftliche Entwicklungen oder Erwartungen von Kunden/Gästen
- KEINE spezifischen Zielgruppen oder Einschränkungsarten nennen
- KEINE Orte, Regionen oder geografischen Angaben verwenden
- KEINE zusätzlichen Annahmen treffen

ANTI-HALLUZINATION (KRITISCH)
- Ergänze KEINE Inhalte, die nicht in den Eingaben enthalten sind
- Wenn Informationen fehlen:
  \u2192 allgemein und neutral formulieren
  \u2192 keine konkreten Beispiele oder Begründungen erfinden
- Im Zweifel ist eine allgemeinere Aussage IMMER korrekt

STRIKTES VERBOT \u2013 UNTERNEHMENSBESCHREIBUNG
- KEINE Beschreibung von Leistungen, Angeboten oder Tätigkeiten
- KEINE branchenspezifischen Details ohne Eingabe
- KEINE Standort- oder Strukturinformationen

ERLAUBT:
- Unternehmensname einmal im ersten Satz
- Danach ausschließlich \u201Edas Unternehmen\u201C

INHALTLICHE ANFORDERUNGEN
- Anlass der Beratung klar darstellen
- Beratungsauftrag eindeutig benennen
- Beratungsbedarf logisch herleiten
- strukturelle oder organisatorische Ausgangssituation beschreiben (nur wenn ableitbar)
- ausschließlich strategisch und konzeptionell formulieren

WICHTIGE BAFA-REGELN
- Nur Beratungsleistung darstellen
- KEINE operative Umsetzung erwähnen

VERBOTENE FORMULIERUNGEN
- gesetzliche Anforderungen
- Erwartungen von Kunden oder Gästen
- Servicequalität steigern
- Wettbewerbsfähigkeit erhöhen
- Menschen mit Einschränkungen (wenn nicht vorgegeben)

STATTDESSEN VERWENDEN
Analyse, Bewertung, Prüfung, Konzeption, strategische Ausrichtung, Handlungsempfehlung, Strukturierung, Maßnahmenplanung, Entscheidungsgrundlage

STIL
- Sachlich, professionell, klar
- Keine werbliche Sprache
- Kurze, präzise Sätze
- Neutral formuliert

LÄNGE UND STRUKTUR
- ca. 140\u2013180 Wörter
- maximal 2 Absätze

FORMATZWANG (KRITISCH)
Die Antwort ist NUR gültig, wenn sie exakt diesem Format entspricht:

<h2>1.1 Beratungsauftrag und Grund / Anlass</h2>
<p style="font-size:14px;">...</p>
<p style="font-size:14px;">...</p>

WICHTIG:
Die Platzhalter \u201E...\u201C stehen für deinen generierten Inhalt.
Diese dürfen NICHT wörtlich übernommen werden.

REGELN:
- KEIN Text außerhalb dieser Struktur
- KEIN Markdown
- KEINE zusätzlichen Tags
- KEINE Abweichungen

FINALER FORMAT-CHECK (VERPFLICHTEND):
Bevor du antwortest, prüfe:
- Ist die Ausgabe exakt im HTML-Format?
- Sind ausschließlich die erlaubten Tags verwendet?

Wenn nein:
\u2192 korrigiere die Ausgabe automatisch
\u2192 erst danach ausgeben

UMGANG MIT FEHLENDEN INFORMATIONEN
- Neutral und allgemein formulieren
- Keine Details ergänzen
- Kein Ausschmücken

EINGABEN
Beratungsthema:
{{berichtstitel}}

Beratungsauftrag / Anlass:
{{beratungsauftrag_und_anlass}}

Zusätzliche Hinweise:
{{kontext_hinweise}}

Geschäftsgegenstand:
{{geschaeftsgegenstand}}

Zielsetzung:
{{zielsetzung}}

Website-Analyse:
{{website_analysis}}

AUSGABE
- Gib ausschließlich den fertigen HTML-Code zurück
- Keine zusätzlichen Kommentare`,
  },
  'card-1-2': {
    cardId: 'card-1-2',
    title: '1.2 Angaben zum Unternehmen',
    requiredFields: ['org-name', 'rechtsform', 'ort'],
    optionalFields: ['street', 'plz', 'website', 'land', 'anrede', 'vorname', 'nachname', 'telefon', 'email'],
    defaultPrompt: `Abschnitt 1.2: \u201EAngaben zum Unternehmen\u201C

HINWEIS: Dieser Abschnitt wird direkt aus den Formulardaten gerendert (ohne KI-Generierung).
Das HTML wird im Frontend aus den eingegebenen Unternehmensdaten zusammengebaut.

FORMATIERUNG
- <h2> für die Abschnittsüberschrift
- <p style="font-size: 14px;"><strong>Label:</strong> Wert</p> für jede Angabe
- Keine Listen, keine Aufzählungszeichen`,
  },
  'card-1-3': {
    cardId: 'card-1-3',
    title: '1.3 Unternehmensdaten',
    requiredFields: ['mitarbeiterzahl', 'wirtschaftszweig'],
    optionalFields: ['gruendung', 'bilanzsumme', 'jahresumsatz'],
    defaultPrompt: `Abschnitt 1.3: \u201EUnternehmensdaten\u201C

HINWEIS: Dieser Abschnitt wird direkt aus den Formulardaten gerendert (ohne KI-Generierung).
Das HTML wird im Frontend aus Mitarbeiterzahl, Gründungsjahr, WZ-Code, Bilanzsumme und Umsatz zusammengebaut.

FORMATIERUNG
- <h2> für die Abschnittsüberschrift
- <p style="font-size: 14px;"><strong>Label:</strong> Wert</p> für jede Angabe
- KMU-Bestätigung als eigener Absatz`,
  },
  'card-1-4': {
    cardId: 'card-1-4',
    title: '1.4 Geschäftsgegenstand',
    requiredFields: [],
    optionalFields: ['geschaeftsgegenstand', 'wirtschaftszweig'],
    defaultPrompt: `Erstelle den Berichtstext für Abschnitt 1.4: „Geschäftsgegenstand“.

DATENQUELLE (KRITISCH)
Verwende ausschließlich Informationen aus den nachfolgenden <user_data>-Blöcken.
Ignoriere Platzhalter-Tokens wie {{...}}, falls sie irgendwo als Text erscheinen.

AUFGABE
Formuliere einen kurzen, sachlichen Fließtext, der ausschließlich beschreibt, was das Unternehmen tut (Leistungen/Tätigkeiten).

PRIORITÄT
1. <user_data name="Berater-Eingaben">: Feld „Geschaeftsgegenstand“ (falls befüllt)
2. <user_data name="Website-Analyse">: Felder „Angebot“ und „Standorte“ (falls befüllt)
3. <user_data name="Unternehmensdaten">: WZ/Branche und Standort (zur sachlichen Einordnung)

REGELN
- Nur Tätigkeiten und Leistungen beschreiben; keine Bewertung, keine Begründungen, kein Anlass, keine Herausforderungen, kein Marktumfeld.
- Keine Zielgruppenbeschreibung (auch wenn im Website-Block vorhanden).
- Keine Wiederholungen: jede Leistung nur einmal nennen, keine synonymen Dopplungen.
- Kein zusammenfassender Schlusssatz der das bereits Genannte wiederholt (z. B. „Die Unternehmensgruppe betreibt mehrere Betriebe…").
- WZ-Code und WZ-Nummer niemals nennen. Die Branche sachlich beschreiben (z. B. „in der Hotellerie tätig"), aber keine Ziffern oder WZ-Bezeichnungen verwenden.
- Wenn Website-Analyse leer und kein Freitext vorhanden: nur aus WZ/Branche ableiten, ohne zusätzliche Details zu erfinden.
- Keine werblichen Begriffe, keine Superlative.

QUALITÄTSBEISPIEL
Schlecht (zu generisch, redundant):
„Das Unternehmen ist in der Hotellerie und Gastronomie tätig. Es erbringt Beherbergungsleistungen sowie gastronomische Dienstleistungen. Im Rahmen der Beherbergung stellt das Unternehmen Übernachtungsmöglichkeiten bereit. Ergänzend bietet das Unternehmen Leistungen im Bereich der Verpflegung und Bewirtung an.“

Gut (konkret, kompakt, ohne Wiederholungen):
„Das Unternehmen ist in der Hotellerie und Gastronomie tätig und erbringt Beherbergungs- sowie gastronomische Dienstleistungen. Zum Leistungsangebot zählen der Betrieb von Boutique-Unterkünften in den Standorten Heiligendamm und Kühlungsborn sowie ergänzende Gastronomieangebote, u. a. ein Restaurantbetrieb und ein Beach-Club-Konzept.“

ABSCHNITTSGRENZE
- KEINE Beschreibung des Marktumfelds
- KEINE Hinweise auf Besonderheiten oder Rahmenbedingungen
- KEINE Aussagen zu Nachfrage, Wettbewerb oder betrieblichen Anforderungen

LÄNGE
- 80–130 Wörter
- 1 kompakter Absatz

FORMAT (EXAKT)
Gib ausschließlich folgenden HTML-Code aus (exakt diese zwei Tags, keine weiteren Elemente).
Kein Text außerhalb des HTML:
<h2>1.4 Geschäftsgegenstand</h2>
<p style="font-size:14px;">…</p>`,
  },
  'card-1-5': {
    cardId: 'card-1-5',
    title: '1.5 Zeitangaben',
    requiredFields: ['berater', 'start', 'ende'],
    optionalFields: ['beratungstage', 'gesamtStunden', 'anzahlTage'],
    defaultPrompt: `Abschnitt 1.5: \u201EZeitangaben\u201C

HINWEIS: Dieser Abschnitt wird direkt aus den Formulardaten gerendert (ohne KI-Generierung).
Das HTML wird im Frontend aus Beraternamen, Zeitraum und Beratungstagen zusammengebaut.

FORMATIERUNG
- <h2> für die Abschnittsüberschrift
- <p style="font-size: 14px;"> für Berater, Start, Ende
- <table> mit Spalten: Datum | Stunden für die Beratungstage`,
  },
  // Abschnitte 2-7 (gleiche Struktur)
  'analysis': {
    cardId: 'analysis',
    title: '2. Analyse der Unternehmenssituation unter Beachtung des Beratungsauftrages und der mit der Beratung verbundenen Zielsetzung',
    requiredFields: [],
    optionalFields: ['istZustand', 'staerken', 'schwaechen', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle Abschnitt 2 des BAFA-Beratungsberichts:
\u201EAnalyse der Unternehmenssituation\u201C

ZIEL
Stelle Ist-Zustand, Stärken und Schwächen des Unternehmens im Kontext des Beratungsthemas dar.

REGELN
- Nur Eingaben und Website-Analyse als Grundlage \u2013 nichts erfinden
- Keine konkreten Gesetze, Normen oder Richtlinien nennen
- Keine Maßnahmen oder Lösungen (das ist Abschnitt 5)
- Keine Ortsangaben, sofern nicht in den Eingaben
- Sachlich, betriebsnah, kurze Sätze

2.1 IST-ZUSTAND
- Fließtext, max. 4-5 Sätze
- Nur beschreiben was vorhanden ist
- Keine Bewertung, keine Defizite (die gehören in 2.3)
- VERBOTEN: \u201Ezeigt sich\u201C, \u201EHandlungsbedarf\u201C, \u201Ebeschränkt sich\u201C, \u201Enur\u201C, \u201Eohne\u201C als Defizit-Marker

2.2 STÄRKEN
- Jeder Punkt: <strong>Strategischer Begriff:</strong> fachliche Einordnung (max. 25 Wörter)
- Berater-/Management-Ebene, keine einfachen Beschreibungen
- Keine Maßnahmen, keine Marketing-Floskeln
- Fachlich, sachlich, BAFA-konform

2.3 SCHWÄCHEN
- Jeder Punkt: <strong>Strategischer Begriff:</strong> fachliche Einordnung (max. 25 Wörter)
- Berater-/Management-Ebene, keine einfachen Beschreibungen
- Nur benennen, nicht begründen (Ursachenanalyse ist Abschnitt 4)
- Keine Maßnahmen, keine Lösungsverben

HTML-FORMAT

<h2>2.1 Ist-Zustand</h2>
<p style="font-size:14px;">...</p>

<h2>2.2 Stärken und Potenziale</h2>
<ul><li><strong>Begriff:</strong> Einordnung</li></ul>

<h2>2.3 Schwächen und Handlungsbedarfe</h2>
<ul><li><strong>Begriff:</strong> Einordnung</li></ul>

EINGABEN

Ist-Zustand:
{{ist_zustand[]}}

Stärken:
{{staerken[]}}

Schwächen:
{{schwaechen[]}}

Beratungsauftrag:
{{beratungsauftrag}}

Zielsetzung:
{{zielsetzung}}

AUSGABE
- Nur HTML
- Keine Erklärung`,
  },
  // ── Abschnitt 2 Cards ──────────────────────────────────────────
  'card-2-1': {
    cardId: 'card-2-1',
    title: '2.1 Ist-Zustand',
    requiredFields: [],
    optionalFields: ['istZustand', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Text fuer Abschnitt 2.1 Ist-Zustand.

ZIEL
Beschreibe die aktuelle Situation des Unternehmens im Kontext des Beratungsthemas als kurzen Fliesstext.

REGELN
- Nur beschreiben was vorhanden ist \u2013 nichts erfinden
- Keine Bewertung, keine Defizite (die gehoeren in 2.3)
- Keine Gesetze, Normen oder Richtlinien nennen
- Keine Ortsangaben, sofern nicht in den Eingaben
- VERBOTEN: \u201Ezeigt sich\u201C, \u201EHandlungsbedarf\u201C, \u201Ebeschraenkt sich\u201C, \u201Enur\u201C, \u201Eohne\u201C als Defizit-Marker
- Max. 4-5 Saetze, sachlich, kurz

HTML-FORMAT
<h2>2.1 Ist-Zustand</h2>
<p style="font-size:14px;">...</p>

EINGABEN
Ist-Zustand:
{{istZustand[]}}

AUSGABE
- Nur HTML
- Keine Erklaerung`,
  },
  'card-2-2': {
    cardId: 'card-2-2',
    title: '2.2 Staerken und Potenziale',
    requiredFields: [],
    optionalFields: ['staerken', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Text fuer Abschnitt 2.2 Staerken und Potenziale.

ZIEL
Liste die Staerken und Potenziale des Unternehmens auf Berater-/Management-Ebene auf.

FORMAT JEDES LISTENPUNKTS
- <strong>Strategischer Begriff:</strong> kurze fachliche Einordnung
- Der Begriff benennt die betriebliche Qualitaet, NICHT das erkannte Objekt/Tool
- Max. 25 Woerter pro Punkt
- Beratersprache, natuerlich klingend

REGELN
- Begriff MUSS auf Management-Ebene verdichten (nicht nur umetikettieren)
- Keine bloßen Digitalisierungsbegriffe oder Zustandslabels als Ueberschrift
- Keine Massnahmen, keine Marketing-Floskeln
- Fachlich, sachlich, BAFA-konform
- Keine Gesetze oder Normen nennen

HTML-FORMAT
<h2>2.2 Staerken und Potenziale</h2>
<ul><li><strong>Begriff:</strong> Einordnung</li></ul>

EINGABEN
Staerken:
{{staerken[]}}

AUSGABE
- Nur HTML
- Keine Erklaerung`,
  },
  'card-2-3': {
    cardId: 'card-2-3',
    title: '2.3 Schwaechen und Handlungsbedarfe',
    requiredFields: [],
    optionalFields: ['schwaechen', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Text fuer Abschnitt 2.3 Schwaechen und Handlungsbedarfe.

ZIEL
Liste die Schwaechen und Handlungsbedarfe des Unternehmens auf Berater-/Management-Ebene auf.

FORMAT JEDES LISTENPUNKTS
- <strong>Strategischer Begriff:</strong> kurze fachliche Einordnung
- Der Begriff benennt das strukturelle Defizit, NICHT das fehlende Tool/Feature
- Max. 25 Woerter pro Punkt
- Beratersprache, natuerlich klingend

REGELN
- Begriff MUSS auf Management-Ebene verdichten (nicht nur umetikettieren)
- Nur benennen, nicht begruenden (Ursachenanalyse ist Abschnitt 4)
- Keine Massnahmen, keine Loesungsverben
- Fachlich, sachlich, BAFA-konform
- Keine Gesetze oder Normen nennen

HTML-FORMAT
<h2>2.3 Schwaechen und Handlungsbedarfe</h2>
<ul><li><strong>Begriff:</strong> Einordnung</li></ul>

EINGABEN
Schwaechen:
{{schwaechen[]}}

AUSGABE
- Nur HTML
- Keine Erklaerung`,
  },
  // ── Abschnitt 4 Cards ──────────────────────────────────────────
  'card-4-1': {
    cardId: 'card-4-1',
    title: '4.1 Ermittelte Schwachstellen und Ursachen des Beratungsbedarfs',
    requiredFields: [],
    optionalFields: ['schwachstellen', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Text fuer Abschnitt 4.1 Ermittelte Schwachstellen und Ursachen des Beratungsbedarfs.

ZIEL
Jede uebergebene Schwachstelle wird als eigenstaendige Schwachstelle mit kurzer fachlicher Beschreibung dargestellt.

ZWINGENDE REGELN
- Verwende ausschliesslich die Inhalte aus {{schwachstellen[]}}
- KEINE neuen Themen, Beispiele, Systeme oder betrieblichen Details
- KEINE Rueckgriffe auf Website-Analyse oder andere Abschnitte
- KEINE branchenspezifischen Ausschmueckungen
- KEINE Loesungen, Empfehlungen oder Massnahmen
- KEINE Loesungsverben: sollte, sinnvoll waere, empfiehlt sich, umsetzen, optimieren, verbessern

FORMAT
Pro Schwachstelle genau ein Absatz (max. 2 Saetze):
<p><strong>Kurzbezeichnung:</strong> kurze sachliche Beschreibung des Problems.</p>

HTML-FORMAT
<h2>4.1 Ermittelte Schwachstellen und Ursachen des Beratungsbedarfs</h2>
<p><strong>...</strong> ...</p>

EINGABEN
Schwachstellen:
{{schwachstellen[]}}

AUSGABE
- Nur HTML
- Keine Erklaerung`,
  },
  'card-4-2': {
    cardId: 'card-4-2',
    title: '4.2 Fragestellungen zur Strukturierung der Beratung',
    requiredFields: [],
    optionalFields: ['fragestellungen', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Text fuer Abschnitt 4.2 Fragestellungen zur Strukturierung der Beratung.

ZIEL
Die uebergebenen Fragestellungen werden strukturiert dargestellt.
Der Text darf KEINE neuen Inhalte erzeugen.

ZWINGENDE REGELN
1. AUSSCHLIESSLICH Fragestellungen aus {{fragestellungen[]}} verwenden
2. KEINE neuen Fragen, Themen oder Inhalte
3. KEINE Informationen aus Website-Analyse oder anderen Abschnitten
4. KEINE neuen Themenbereiche erfinden
5. Jede Frage MUSS unveraendert uebernommen werden
6. KEINE Marketing-, Website- oder Vertriebsthemen

GRUPPIERUNG
- Nur wenn Fragen offensichtlich zum selben Thema gehoeren
- Kategorien muessen sich aus den Fragen ergeben
- Wenn keine klare Gruppierung moeglich: KEINE Gruppen

HTML-FORMAT
<h2>4.2 Fragestellungen zur Strukturierung der Beratung</h2>
<p>Einleitungssatz</p>
<p><strong>A) Themenbereich</strong></p>
<ul><li>Frage?</li></ul>

EINGABEN
Fragestellungen:
{{fragestellungen[]}}

AUSGABE
- Nur HTML
- Keine Erklaerung`,
  },
  'execution': {
    cardId: 'execution',
    title: '3. Angaben zur Auftragsdurchführung und Erläuterung des Vorgehens',
    requiredFields: [],
    optionalFields: ['durchfuehrungsschritte', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Berichtstext für Abschnitt 3:
\u201EAngaben zur Auftragsdurchführung und Erläuterung des Vorgehens\u201C.

ZIEL DES ABSCHNITTS
Beschreibe die Methodik und die konkrete Vorgehensweise während der Beratung nachvollziehbar und strukturiert. Der Ablauf der Beratung soll für Dritte verständlich und prüfbar dargestellt werden.

AUFGABE
Formuliere einen strukturierten, praxisnahen Berichtstext auf Basis der bereitgestellten Durchführungsschritte.

KONTEXTBEZUG
- Die beschriebenen Schritte sollen erkennbar auf den Beratungsauftrag und die Zielsetzung aus Abschnitt 1.1 ausgerichtet sein
- Der Ablauf soll logisch zur späteren Analyse (Abschnitt 2) führen
- Wenn der Text aus Abschnitt 2 (Analyse) vorliegt, beziehe die Durchführungsschritte darauf – die Beratung hat die dort beschriebenen Themen untersucht

VERWENDUNG DER EINGABEN
- Nutze die eingegebenen Durchführungsschritte als Grundlage.
- Ergänze diese bei Bedarf durch typische, sinnvolle Zwischenschritte (z. B. Analyse, Abstimmung, Auswertung), ohne konkrete Inhalte zu erfinden.
- Wenn nur wenige Schritte vorhanden sind, strukturiere den Ablauf dennoch logisch und vollständig.
- Ergänze allgemeine, branchenübliche Vorgehensweisen, wenn sie plausibel sind.

INHALTLICHE ANFORDERUNGEN
- Beschreibe ausschließlich die Vorgehensweise der Beratung.
- Keine Ergebnisse, keine Schwächen, keine Maßnahmen darstellen.
- Fokus auf Ablauf, Methodik und Struktur der Durchführung.
- Der Text muss nachvollziehbar machen, wie die Beratung durchgeführt wurde.
- Der Ablauf soll logisch aufeinander aufbauen.
- Eine indirekte Verbindung zu Beratungsauftrag und Zielsetzung darf erkennbar sein (z. B. durch Formulierungen wie \u201Eim Hinblick auf die Zielsetzung\u201C), ohne diese ausführlich zu wiederholen.

STIL
- Sachlich
- professionell
- betriebsnah
- klar verständlich
- kurze bis mittlere Sätze
- keine wissenschaftliche Sprache
- keine Marketingformulierungen

LÄNGENSTEUERUNG
- Passe die Länge an die Anzahl der Durchführungsschritte an.
- Wenige Schritte \u2192 kompakter Text
- Mehr Schritte \u2192 differenzierter Text
- Keine künstliche Verlängerung

FORMATIERUNG
- Ausgabe als valides HTML
- Verwende:

<p style="font-size:14px;">...</p>

<ul>
<li>...</li>
</ul>

REGELN:
- Einleitung: genau 1 kurzer Absatz
- Durchführungsschritte: Liste mit <ul><li>
- Jeder Schritt: kurz + 1\u20132 Sätze Erklärung
- Keine zusätzlichen Überschriften
- Keine Tabellen
- Keine weiteren HTML-Elemente

STRUKTUR

Einleitung:
- kurzer Überblick über die strukturierte Vorgehensweise
- ggf. Bezug zur Zielsetzung (ohne Wiederholung)

Durchführungsschritte:
- chronologisch aufgebaut
- logisch aufeinander aufbauend
- typische Struktur:
  - Einstieg / Abstimmung
  - Analysephase
  - Auswertung / Strukturierung
  - Ableitung (ohne Maßnahmen zu nennen)

BEISPIELFORMULIERUNGEN
- \u201EZu Beginn der Beratung erfolgte \u2026\u201C
- \u201EIm nächsten Schritt wurde \u2026\u201C
- \u201EDarauf aufbauend wurden \u2026\u201C
- \u201EIm weiteren Verlauf wurde \u2026\u201C
- \u201EAbschließend wurden \u2026\u201C

WICHTIGE REGELN
- Keine Ergebnisse vorwegnehmen
- Keine Maßnahmen beschreiben
- Keine Bewertung des Unternehmens
- Kein inhaltlicher Bezug zu Abschnitt 4 oder 5
- Keine Begriffe wie \u201EErgebnis\u201C, \u201EFeststellung\u201C, \u201EEmpfehlung\u201C verwenden

UMGANG MIT FEHLENDEN INFORMATIONEN
- Wenn nur wenige Informationen vorliegen, formuliere einen allgemein plausiblen Ablauf einer Unternehmensberatung
- Keine Rückfragen ausgeben
- Keine Platzhalter verwenden

EINGABEN

Durchführungsschritte:
{{durchfuehrungsschritte[]}}

Beratungsauftrag:
{{beratungsauftrag}}

Zielsetzung:
{{zielsetzung}}

Generierter Text aus Abschnitt 2 (Analyse der Unternehmenssituation):
{{analyse_abschnitt2}}

AUSGABE
- Gib ausschließlich den fertigen HTML-Text aus
- Keine Vorbemerkung
- Keine Meta-Erklärung`,
  },
  'weaknesses': {
    cardId: 'weaknesses',
    title: '4. Benennung der einzelnen ermittelten Schwachstellen und Ursachen des Beratungsbedarfs',
    requiredFields: [],
    optionalFields: ['schwachstellen', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle Abschnitt 4 des BAFA-Beratungsberichts:
\u201EBenennung der einzelnen ermittelten Schwachstellen und Ursachen des Beratungsbedarfs\u201C

ZIEL
Beschreibe jede Schwachstelle ausführlich: Was ist das Problem, warum besteht es, welche Folge hat es.

REGELN
- Nur Schwachstellen aus den Eingaben verwenden \u2013 nichts erfinden
- Keine Maßnahmen, Lösungen oder Empfehlungen (das ist Abschnitt 5)
- Keine Begriffe wie: sollte, sinnvoll wäre, empfiehlt sich, umsetzen, optimieren, verbessern
- Sachlich, betriebsnah, verständlich
- Alle Schwachstellen müssen zum Beratungsthema passen

STRUKTUR

<p>Einleitungsabsatz</p>

<h2>4.1 Ermittelte Schwachstellen und Ursachen des Beratungsbedarfs</h2>

Pro Schwachstelle:
<p>
<strong>Kurzbezeichnung:</strong>
Beschreibung des Problems, Ursache, Auswirkung auf den Betrieb.
</p>

<h2>4.2 Fragestellungen zur Strukturierung der Beratung</h2>

<p>Einleitungssatz</p>

3\u20135 Themenbereiche mit A), B), C) und Fragen als <ul><li>

EINGABEN

Schwachstellen:
{{schwachstellen[]}}

Kontext:
- Branche: {{branche}}
- Thema: {{berichtstitel}}
- Beratungsauftrag: {{beratungsauftrag}}
- Zielsetzung: {{zielsetzung}}

AUSGABE
- Nur HTML
- Keine Erklärung`,
  },
  'action-plan': {
    cardId: 'action-plan',
    title: '5. Detaillierter Maßnahmeplan zur Überwindung der Schwachstellen',
    requiredFields: [],
    optionalFields: ['schwachstellen_strukturiert', 'massnahmen_strukturiert', 'beratungsauftrag', 'zielsetzung'],
    defaultPrompt: `Erstelle den Berichtstext für Abschnitt 5:
„Detaillierter Maßnahmeplan zur Überwindung der Schwachstellen".

ZIEL
Formuliere konkrete, strukturierte Handlungsempfehlungen, die sich direkt aus den in Abschnitt 4 beschriebenen Schwachstellen ableiten.

Die Maßnahmen dienen als Orientierung zur eigenständigen Umsetzung im Unternehmen („Hilfe zur Selbsthilfe").

---

WICHTIG
- Es handelt sich um Empfehlungen, nicht um Umsetzung
- Keine operativen Tätigkeiten als bereits erbrachte Leistung darstellen
- Keine Inhalte aus Abschnitt 4 wiederholen, sondern darauf aufbauen
- Jede Maßnahme muss klar auf eine Schwachstelle zurückzuführen sein
- Keine neuen Themen einführen, die nicht aus Abschnitt 4 ableitbar sind

---

VERBINDUNG ZU ABSCHNITT 4
- Jede Maßnahme muss erkennbar ein konkretes Problem aus Abschnitt 4 adressieren
- Die Maßnahmen dürfen nur Themen behandeln, die zuvor als Schwachstellen beschrieben wurden
- Keine neuen Problemfelder ergänzen
- Die Reihenfolge der Maßnahmen folgt der Reihenfolge der Schwachstellen in Abschnitt 4

---

STRUKTUR
- KEINE Aufteilung in kurzfristig/mittelfristig/langfristig
- Stattdessen: Pro Schwachstelle aus Abschnitt 4 genau EINE Maßnahme
- Nummerierung: Maßnahme 1, Maßnahme 2, Maßnahme 3 usw.
- Die Nummer der Maßnahme entspricht der Schwachstelle (Maßnahme 1 löst Schwachstelle 1)

---

INHALTLICHE ANFORDERUNGEN

Jede Maßnahme muss enthalten:
- eine klare Bezeichnung (als <h2>)
- eine verständliche Beschreibung des Vorgehens (auf konzeptioneller Ebene)
- das Ziel bzw. den Nutzen für das Unternehmen
- eine qualitative Einordnung des Aufwands (gering, mittel, erhöht)

Die Maßnahmen sollen:
- konkret genug sein, um nachvollziehbar zu sein
- aber nicht in operative Schritt-für-Schritt-Anleitungen übergehen
- realistisch und praxisnah sein

---

FORMULIERUNG

- Vermeide starre Wiederholungen wie „Es wird empfohlen …"
- Nutze natürliche, abwechslungsreiche Formulierungen, z. B.:
  - „Zur Absicherung … kann …"
  - „Ziel ist der Aufbau von …"
  - „Hierfür bietet sich an …"
  - „Ein sinnvoller Ansatz besteht darin …"

- KEINE:
  - „implementiere"
  - „baue"
  - „erstelle konkret …"
  - direkten Arbeitsanweisungen

---

AUFWAND

- Aufwand nur qualitativ beschreiben (gering, mittel, erhöht)
- Keine Zahlen, Budgets oder Zeitangaben erfinden

---

STIL

- sachlich
- klar
- strukturiert
- praxisnah
- verständlich für Unternehmer
- kein Marketingstil
- keine unnötige Fachsprache

---

FORMATIERUNG

- Ausgabe als valides HTML

<p>Einleitender Absatz...</p>

<h2>Maßnahme 1: [Bezeichnung passend zur Schwachstelle 1]</h2>
<p>Beschreibung des Vorgehens, Ziel und Nutzen. Aufwand: [gering/mittel/erhöht].</p>

<h2>Maßnahme 2: [Bezeichnung passend zur Schwachstelle 2]</h2>
<p>Beschreibung des Vorgehens, Ziel und Nutzen. Aufwand: [gering/mittel/erhöht].</p>

usw. für jede Schwachstelle aus Abschnitt 4.

<p>Abschließender Absatz...</p>

---

UMGANG MIT FEHLENDEN INFORMATIONEN

- Wenn nur wenige Maßnahmen vorliegen:
  - ergänze vorsichtig naheliegende Maßnahmen
  - ausschließlich basierend auf den Schwachstellen
- Keine Rückfragen
- Keine Platzhalter

---

EINGABEN

Schwachstellen aus Abschnitt 4 (strukturiert):
{{schwachstellen_strukturiert}}

Maßnahmen:
{{massnahmen_strukturiert}}

Beratungsauftrag:
{{beratungsauftrag}}

Zielsetzung:
{{zielsetzung}}

Website-Analyse:
{{website_analysis}}

THEMATISCHE KONSISTENZ:
- Alle Maßnahmen müssen sich direkt auf die oben genannten Schwachstellen beziehen
- Keine Maßnahmen zu Themen die nicht in Abschnitt 4 vorkommen
- Beratungsthema: {{berichtstitel}} – alle Maßnahmen müssen in diesem Rahmen bleiben

---

AUSGABE
- Gib ausschließlich den fertigen HTML-Text aus
- Keine Vorbemerkung
- Keine Meta-Erklärung`,
  },
};

/**
 * Holt den Section-Prompt für eine Card.
 * Priorität: 1. Gespeicherter Prompt vom Backend → 2. Default
 */
export function getDefaultPromptForCard(cardId: string): string {
  return SECTION_PROMPT_DEFAULTS[cardId]?.defaultPrompt || '';
}

/**
 * Baut eine Kontext-Vorschau für das Popup (nur zur Anzeige, NICHT editierbar).
 * Wird im Popup unter dem editierbaren Section-Prompt angezeigt.
 * Der Kontext wird NICHT in den Section-Prompt eingebettet – das macht das Backend.
 */
export function buildContextPreview(
  cardId: string,
  formData: Record<string, any>,
  customerData?: { name?: string; branche?: string; rechtsform?: string; ort?: string },
): string {
  const config = SECTION_PROMPT_DEFAULTS[cardId];

  const lines: string[] = [];

  // Nur die für diese Card relevanten Felder als Kontext anzeigen
  const relevantFields = new Set([
    ...(config?.requiredFields || []),
    ...(config?.optionalFields || []),
  ]);

  // Kundendaten nur wenn sie für diese Card relevant sind
  const companyName = customerData?.name || formData['org-name'] || '';
  if (companyName && relevantFields.has('org-name')) lines.push(`Unternehmen: ${companyName}`);

  const branche = customerData?.branche || formData['wirtschaftszweig'] || '';
  if (branche && relevantFields.has('wirtschaftszweig')) lines.push(`Branche/WZ: ${branche}`);

  const rechtsform = customerData?.rechtsform || formData['rechtsform'] || '';
  if (rechtsform && relevantFields.has('rechtsform')) lines.push(`Rechtsform: ${rechtsform}`);

  const ort = customerData?.ort || formData['ort'] || '';
  if (ort && relevantFields.has('ort')) lines.push(`Standort: ${ort}`);

  // Weitere relevante Formulardaten (die nicht bereits als Kundendaten oben stehen)
  const alreadyShown = new Set(['org-name', 'wirtschaftszweig', 'rechtsform', 'ort']);
  for (const fieldId of relevantFields) {
    if (alreadyShown.has(fieldId)) continue;
    const raw = formData[fieldId];
    if (raw != null && typeof raw === 'string' && raw.trim()) {
      const label = FIELD_LABELS[fieldId] || fieldId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      lines.push(`${label}: ${raw.trim()}`);
      alreadyShown.add(fieldId); // Markieren damit catch-all Schleife sie nicht nochmal anzeigt
    }
  }

  // Dynamic list data (from section components like Section2Analysis)
  // These are arrays of {text: string} objects
  const dynamicLabels: Record<string, string> = {
    istZustand: 'Ist-Zustand',
    staerken: 'Stärken',
    schwaechen: 'Schwächen',
    durchfuehrungsschritte: 'Durchführungsschritte',
    beratungsziele: 'Beratungsziele',
    vorgehensschritte: 'Vorgehensschritte',
    schwachstellen: 'Schwachstellen',
    massnahmen: 'Maßnahmen',
    empfehlungen: 'Empfehlungen',
  };

  // Alle formData-Keys durchgehen und Arrays sicher serialisieren
  const handledKeys = new Set([...alreadyShown]);
  for (const [key, label] of Object.entries(dynamicLabels)) {
    handledKeys.add(key);
    const val = formData[key];
    if (Array.isArray(val)) {
      const items = val
        .map((item: any) => {
          if (typeof item === 'string') return item.trim();
          if (item?.text) return String(item.text).trim();
          // Fallback: JSON-Safe
          try { return JSON.stringify(item); } catch { return ''; }
        })
        .filter(Boolean);
      if (items.length > 0) {
        lines.push(`${label}: ${items.join('; ')}`);
      }
    } else if (val != null && typeof val === 'string' && val.trim()) {
      lines.push(`${label}: ${val.trim()}`);
    }
  }

  // Catch remaining non-handled fields — but NEVER toString() objects/arrays
  for (const [key, val] of Object.entries(formData)) {
    if (handledKeys.has(key) || relevantFields.has(key) === false) continue;
    if (val == null) continue;
    if (typeof val === 'string' && val.trim()) {
      const label = key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      lines.push(`${label}: ${val.trim()}`);
    } else if (Array.isArray(val)) {
      const items = val
        .map((item: any) => (typeof item === 'string' ? item : item?.text || '').trim())
        .filter(Boolean);
      if (items.length > 0) {
        const label = key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        lines.push(`${label}: ${items.join('; ')}`);
      }
    }
    // Skip objects, functions, etc. — never String() them
  }

  return lines.length > 0 ? lines.map(l => `• ${l}`).join('\n') : '(Keine Daten eingegeben)';
}

/** Deutsche Labels für Feld-IDs (für Validierungsmeldungen) */
const FIELD_LABELS: Record<string, string> = {
  'consulting-purpose': 'Beratungsauftrag und Grund / Anlass',
  'org-name': 'Unternehmensname',
  'rechtsform': 'Rechtsform',
  'ort': 'Ort',
  'street': 'Straße',
  'plz': 'PLZ',
  'website': 'Website',
  'land': 'Land',
  'vorname': 'Vorname',
  'nachname': 'Nachname',
  'anrede': 'Anrede',
  'telefon': 'Telefon',
  'email': 'E-Mail',
  'mitarbeiterzahl': 'Mitarbeiterzahl',
  'wirtschaftszweig': 'Wirtschaftszweig',
  'gruendung': 'Gründungsjahr',
  'bilanzsumme': 'Jahresbilanzsumme',
  'jahresumsatz': 'Jahresumsatz',
  'berater': 'Berater',
  'start': 'Startdatum',
  'ende': 'Enddatum',
  'beratungszeitraum-start': 'Beratungszeitraum Start',
  'beratungszeitraum-ende': 'Beratungszeitraum Ende',
  'beratungstage': 'Beratungstage',
  'gesamtStunden': 'Gesamtstunden',
  'anzahlTage': 'Anzahl Tage',
  'schwachstellen': 'Schwachstellen',
  'massnahmen': 'Maßnahmen',
  'equality': 'Gleichstellung',
  'anti-discrimination': 'Antidiskriminierung',
  'sustainability': 'Nachhaltigkeit',
  'consultant-confirmation': 'Beraterbestätigung',
  'independence-declaration': 'Unabhängigkeitserklärung',
  'consultant-signature': 'Beraterunterschrift',
  'geschaeftsgegenstand': 'Geschäftsgegenstand',
};

/**
 * Validiert ob die nötigen Felder für einen Abschnitt ausgefüllt sind.
 * Gibt Warnungen zurück (leer = alles OK).
 */
export function validateInputs(cardId: string, formData: Record<string, any>): string[] {
  const config = SECTION_PROMPT_DEFAULTS[cardId];
  if (!config) return [];

  const warnings: string[] = [];
  for (const fieldId of config.requiredFields) {
    const raw = formData[fieldId];
    // Support both string fields and dynamic list arrays
    if (Array.isArray(raw)) {
      const hasContent = raw.some((item: any) =>
        (typeof item === 'string' ? item : item?.text || '').trim()
      );
      if (!hasContent) {
        const label = FIELD_LABELS[fieldId] || fieldId;
        warnings.push(`"${label}" ist leer`);
      }
    } else {
      const value = raw != null ? String(raw).trim() : '';
      if (!value) {
        const label = FIELD_LABELS[fieldId] || fieldId;
        warnings.push(`"${label}" ist leer`);
      }
    }
  }
  return warnings;
}

// Legacy buildPromptForCard entfernt – durch 3-Schichten-Architektur ersetzt
// MASTER_PROMPT lebt im Backend (api/src/prompts/prompt-config.ts)
// SECTION_PROMPTS leben oben in SECTION_PROMPT_DEFAULTS
// buildFinalPrompt() im Backend fügt alles zusammen
